/**
 * BrandScreen — formulario "Mi marca". Conectado a `useAuth().updateProfile()`.
 *
 * Sticky footer "Guardar cambios" se anima cuando hay cambios sin guardar.
 * Preview en vivo en sidebar derecho (o arriba en mobile) muestra cómo se
 * verá la marca en el plan compartido.
 *
 * Uploads:
 *   - Foto perfil (`picture_url`) y logo (`brand_logo_url`) usan `<ImageUploader>`,
 *     que sube YA al cropear y mete la URL absoluta en el form. El "Guardar"
 *     persiste el patch al backend. Si el coach cropea pero no guarda, la
 *     imagen queda huérfana en el server (aceptable; no bloquea nada).
 *
 * Brand prefs locales (no persisten en backend, solo localStorage):
 *   - displayFont:  qué font afecta nombre + monograma de la preview
 *   - logoStyle:    visual del monograma (solid / outline / gradient)
 *   - showPhoto:    si la firma usa foto de perfil o solo logo/monograma
 *   Key: `al_brand_prefs`. Si en el futuro se promueven a backend, hay que
 *   migrar leyendo de localStorage la primera vez y enviando un PATCH.
 */
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import { ImageUploader } from "../lib/ImageUploader";
import type { AetherLiteUser } from "../api/auth";
import { applyBrandToPlans } from "../api/auth";
import { listPlans } from "../api/plans";

interface FormState {
  name: string;
  tagline: string;
  brand_letters: string;
  picture_url: string;
  brand_logo_url: string;
  brand_color_primary: string;
  brand_color_accent: string;
  phone: string;
  instagram: string;
  web: string;
  // Defaults de portada (Feature 2) — se precargan en cada plan nuevo
  cover_eyebrow: string;
  cover_subtitle: string;
  cover_duration: string;
}

// ---- Brand prefs locales (localStorage, no van al backend) ----

type DisplayFont = "bricolage" | "inter" | "playfair" | "montserrat" | "jetbrains";
type LogoStyle = "solid" | "outline" | "gradient";

interface BrandPrefs {
  displayFont: DisplayFont;
  logoStyle: LogoStyle;
  showPhoto: boolean;
}

const DEFAULT_PREFS: BrandPrefs = {
  displayFont: "bricolage",
  logoStyle: "solid",
  showPhoto: true,
};

const PREFS_KEY = "al_brand_prefs";

const FONT_FAMILIES: Record<DisplayFont, string> = {
  bricolage: "'Bricolage Grotesque', system-ui, sans-serif",
  inter: "'Inter Tight', 'Inter', system-ui, sans-serif",
  playfair: "'Playfair Display', Georgia, serif",
  montserrat: "'Montserrat', system-ui, sans-serif",
  jetbrains: "'JetBrains Mono', ui-monospace, monospace",
};

const FONT_LABELS: Record<DisplayFont, string> = {
  bricolage: "Bricolage Grotesque",
  inter: "Inter Tight",
  playfair: "Playfair Display",
  montserrat: "Montserrat",
  jetbrains: "JetBrains Mono",
};

function loadPrefs(): BrandPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<BrandPrefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(p: BrandPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* localStorage podría estar deshabilitado — silent fail. */
  }
}

// Inserta el link a Google Fonts para Playfair y Montserrat la primera vez
// que la screen se monta (las otras 3 ya están en index.html). Solo se
// inyecta una vez por carga porque chequeamos un flag en el head.
function ensureExtraFontsLoaded() {
  if (typeof document === "undefined") return;
  const id = "al-brand-extra-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Montserrat:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(link);
}

function defaultLetters(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "AL";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fromUser(u: AetherLiteUser | null): FormState {
  return {
    name: u?.name || "",
    tagline: u?.tagline || "",
    brand_letters: (u?.brand_letters || defaultLetters(u?.name || "")).slice(0, 3).toUpperCase(),
    picture_url: u?.picture_url || "",
    brand_logo_url: u?.brand_logo_url || "",
    brand_color_primary: u?.brand_color_primary || "#7c3aed",
    brand_color_accent: u?.brand_color_accent || "#ec4899",
    phone: u?.phone || "",
    instagram: u?.instagram || "",
    web: u?.web || "",
    cover_eyebrow: u?.cover_eyebrow || "",
    cover_subtitle: u?.cover_subtitle || "",
    cover_duration: u?.cover_duration || "",
  };
}

function diff(a: FormState, b: FormState): Partial<AetherLiteUser> {
  const out: Partial<AetherLiteUser> = {};
  const keys: (keyof FormState)[] = [
    "name", "tagline", "brand_letters", "picture_url", "brand_logo_url",
    "brand_color_primary", "brand_color_accent", "phone", "instagram", "web",
    "cover_eyebrow", "cover_subtitle", "cover_duration",
  ];
  for (const k of keys) {
    if ((a as any)[k] !== (b as any)[k]) {
      const v = (a as any)[k] as string;
      // Strings vacíos los enviamos como null para limpiar el campo en el backend.
      (out as any)[k] = v === "" ? null : v;
    }
  }
  return out;
}

export function BrandScreen() {
  const { user, updateProfile } = useAuth();
  const toast = useToast();
  const initial = useMemo(() => fromUser(user), [user]);
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState<BrandPrefs>(() => loadPrefs());

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  useEffect(() => {
    ensureExtraFontsLoaded();
  }, []);

  useEffect(() => {
    savePrefs(prefs);
  }, [prefs]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((s) => ({ ...s, [k]: v }));
  };

  const setPref = <K extends keyof BrandPrefs>(k: K, v: BrandPrefs[K]) => {
    setPrefs((p) => ({ ...p, [k]: v }));
  };

  const dirty = useMemo(() => {
    const d = diff(form, initial);
    return Object.keys(d).length > 0;
  }, [form, initial]);

  const onSave = async () => {
    if (!dirty || saving) return;
    const patch = diff(form, initial);
    setSaving(true);
    try {
      await updateProfile(patch);
      toast("Marca actualizada");
    } catch (e) {
      toast("No pude guardar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setSaving(false);
    }
  };

  const previewStyle = {
    "--al-prev-primary": form.brand_color_primary,
    "--al-prev-accent": form.brand_color_accent,
    "--al-prev-display-font": FONT_FAMILIES[prefs.displayFont],
  } as CSSProperties;

  const monogramText = (form.brand_letters || defaultLetters(form.name)).slice(0, 3).toUpperCase();
  const showLogo = !prefs.showPhoto && form.brand_logo_url;
  const showPhoto = prefs.showPhoto && form.picture_url;
  const monogramClass = `al-brand-card-monogram al-brand-card-monogram--${prefs.logoStyle}`;

  return (
    <div className="page al-brand">
      <div className="al-brand-grid">
        {/* ---------- Form ---------- */}
        <div className="al-brand-form">
          <Section title="Identidad">
            <Field label="Nombre">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Tu nombre completo" />
            </Field>
            <Field label="Tagline" hint="Aparece debajo de tu nombre en cada plan">
              <input
                value={form.tagline}
                onChange={(e) => set("tagline", e.target.value.toUpperCase())}
                placeholder="ENTRENADOR PERSONAL"
              />
            </Field>
            <Field label="Iniciales / monograma" hint="Hasta 3 letras. Si lo dejas vacío usamos las del nombre.">
              <input
                value={form.brand_letters}
                maxLength={3}
                onChange={(e) => set("brand_letters", e.target.value.toUpperCase())}
                placeholder={defaultLetters(form.name)}
                style={{ maxWidth: 120, textTransform: "uppercase", letterSpacing: "0.1em" }}
              />
            </Field>
          </Section>

          <Section title="Foto y logo">
            <Field label="Foto de perfil" hint="JPEG / PNG / WebP, hasta 20 MB. Podés alejar, acercar, rotar y recortar.">
              <ImageUploader
                value={form.picture_url || null}
                onChange={(url) => set("picture_url", url || "")}
                folder="brand"
                shape="circle"
                size={120}
                label="Subir foto"
              />
            </Field>

            <Field
              label="Logo de marca"
              hint="Aparece en la firma del plan y como marca de agua en cada hoja. PNG con fondo transparente recomendado; si subís JPEG con fondo blanco usamos blend-mode para que se vea limpio en el watermark."
            >
              <ImageUploader
                value={form.brand_logo_url || null}
                onChange={(url) => set("brand_logo_url", url || "")}
                folder="brand"
                shape="square"
                size={120}
                aspect={1}
                label="Subir logo"
              />
            </Field>
          </Section>

          <Section title="Colores">
            <div className="al-brand-colors">
              <Field label="Primario">
                <div className="al-brand-color-row">
                  <input
                    type="color"
                    value={form.brand_color_primary}
                    onChange={(e) => set("brand_color_primary", e.target.value)}
                  />
                  <input
                    value={form.brand_color_primary}
                    onChange={(e) => set("brand_color_primary", e.target.value)}
                    placeholder="#7c3aed"
                  />
                </div>
              </Field>
              <Field label="Acento">
                <div className="al-brand-color-row">
                  <input
                    type="color"
                    value={form.brand_color_accent}
                    onChange={(e) => set("brand_color_accent", e.target.value)}
                  />
                  <input
                    value={form.brand_color_accent}
                    onChange={(e) => set("brand_color_accent", e.target.value)}
                    placeholder="#ec4899"
                  />
                </div>
              </Field>
            </div>
          </Section>

          <Section title="Portada por defecto">
            <p className="hint" style={{ marginTop: -4, marginBottom: 4 }}>
              Estos textos se precargan en la portada de cada plan nuevo, así no
              los reescribís cada vez. Podés editarlos por plan en el editor.
            </p>
            <Field label="Eyebrow" hint="La línea pequeña sobre el título. Ej: 'PLAN DE ENTRENAMIENTO'.">
              <input
                value={form.cover_eyebrow}
                onChange={(e) => set("cover_eyebrow", e.target.value)}
                placeholder="PLAN DE ENTRENAMIENTO"
              />
            </Field>
            <Field label="Subtítulo / descripción" hint="El texto descriptivo bajo el título de la portada.">
              <input
                value={form.cover_subtitle}
                onChange={(e) => set("cover_subtitle", e.target.value)}
                placeholder="Programa personalizado de entrenamiento y nutrición"
              />
            </Field>
            <Field label="Duración típica" hint="Ej: '12 semanas'. Se puede cambiar por plan.">
              <input
                value={form.cover_duration}
                onChange={(e) => set("cover_duration", e.target.value)}
                placeholder="12 semanas"
                style={{ maxWidth: 200 }}
              />
            </Field>
          </Section>

          <Section title="Estilo visual">
            <Field label="Tipografía display" hint="Afecta nombre + monograma en la preview. Solo se guarda local (no se persiste en backend todavía).">
              <select
                value={prefs.displayFont}
                onChange={(e) => setPref("displayFont", e.target.value as DisplayFont)}
                style={{ fontFamily: FONT_FAMILIES[prefs.displayFont] }}
              >
                {(Object.keys(FONT_LABELS) as DisplayFont[]).map((k) => (
                  <option key={k} value={k} style={{ fontFamily: FONT_FAMILIES[k] }}>
                    {FONT_LABELS[k]}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Estilo del monograma">
              <div className="al-brand-radio-group" role="radiogroup" aria-label="Estilo del monograma">
                {(["solid", "outline", "gradient"] as LogoStyle[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={prefs.logoStyle === s}
                    className={"al-brand-radio-chip " + (prefs.logoStyle === s ? "is-active" : "")}
                    onClick={() => setPref("logoStyle", s)}
                  >
                    <span
                      className={`al-brand-radio-swatch al-brand-card-monogram--${s}`}
                      style={previewStyle}
                    >
                      Aa
                    </span>
                    <span className="al-brand-radio-label">
                      {s === "solid" ? "Sólido" : s === "outline" ? "Outline" : "Gradiente"}
                    </span>
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Firma con">
              <div className="al-brand-radio-group" role="radiogroup" aria-label="Firma con foto o logo">
                <button
                  type="button"
                  role="radio"
                  aria-checked={prefs.showPhoto}
                  className={"al-brand-radio-chip " + (prefs.showPhoto ? "is-active" : "")}
                  onClick={() => setPref("showPhoto", true)}
                >
                  <Icon name="user" size={14} />
                  <span className="al-brand-radio-label">Foto de perfil</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!prefs.showPhoto}
                  className={"al-brand-radio-chip " + (!prefs.showPhoto ? "is-active" : "")}
                  onClick={() => setPref("showPhoto", false)}
                >
                  <Icon name="sparkle" size={14} />
                  <span className="al-brand-radio-label">Solo logo / monograma</span>
                </button>
              </div>
            </Field>
          </Section>

          <Section title="Contacto">
            <Field label="Teléfono / WhatsApp">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+57 300 000 0000" />
            </Field>
            <Field label="Instagram">
              <input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@tuusuario" />
            </Field>
            <Field label="Sitio web">
              <input value={form.web} onChange={(e) => set("web", e.target.value)} placeholder="https://…" />
            </Field>
          </Section>

          {/* Sincronizar marca con planes existentes — caso clásico: el coach
              creó plantillas ANTES de completar Mi marca, ahora quiere que
              esos planes hereden su instagram, teléfono, logo, etc. */}
          <Section title="Sincronizar con planes existentes">
            <SyncBrandToPlans />
          </Section>
        </div>

        {/* ---------- Preview ---------- */}
        <aside className="al-brand-preview" style={previewStyle}>
          <div className="stat-label">Vista previa</div>
          <div className="al-brand-card">
            <div className="al-brand-card-top">
              {showPhoto ? (
                <img src={form.picture_url} alt="" className="al-brand-card-photo" />
              ) : showLogo ? (
                <img src={form.brand_logo_url} alt="" className="al-brand-card-logo" />
              ) : (
                <div className={monogramClass}>{monogramText}</div>
              )}
              <div className="al-brand-card-text">
                <div className="al-brand-card-name">{form.name || "Tu nombre"}</div>
                <div className="al-brand-card-tagline">
                  {form.tagline || "ENTRENADOR PERSONAL"}
                </div>
              </div>
            </div>
            <div className="al-brand-card-bar" />
            <div className="al-brand-card-meta">
              {form.instagram && <span>· {form.instagram}</span>}
              {form.web && <span>· {form.web.replace(/^https?:\/\//, "")}</span>}
              {form.phone && <span>· {form.phone}</span>}
            </div>
          </div>
          <div className="tiny muted" style={{ marginTop: 12 }}>
            Así se verá tu firma en cada plan compartido.
          </div>
        </aside>
      </div>

      {/* ---------- Sticky footer ---------- */}
      <div className={"al-brand-footer " + (dirty ? "is-dirty" : "")}>
        <div className="muted tiny">
          {dirty ? "Tienes cambios sin guardar" : "Todo al día"}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={() => setForm(initial)} disabled={!dirty || saving}>
          Descartar
        </button>
        <button className="btn btn-primary" onClick={onSave} disabled={!dirty || saving}>
          <Icon name="check" size={14} /> {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}

/**
 * SyncBrandToPlans — botón + modal de confirmación para aplicar la marca
 * actual del coach a sus planes existentes.
 *
 * Modos:
 *   - fill_empty (default seguro): solo escribe los campos del brand del
 *     plan que estén vacíos. No pisa branding personalizado por plan.
 *   - override: reemplaza los campos identitarios del brand (preserva el
 *     estilo editorial — theme, fuentes, densidad). Para coaches que cambiaron
 *     su logo/colores y quieren actualizar TODOS los planes.
 */
function SyncBrandToPlans() {
  const toast = useToast();
  const [plansCount, setPlansCount] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"fill_empty" | "override">("fill_empty");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listPlans()
      .then((p) => setPlansCount(p.length))
      .catch(() => setPlansCount(null));
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const res = await applyBrandToPlans(mode);
      toast(
        res.updated_count === 0
          ? "Tus planes ya tenían toda tu marca aplicada"
          : `Marca aplicada a ${res.updated_count} plan${res.updated_count === 1 ? "" : "es"}`,
      );
      setOpen(false);
    } catch (e) {
      toast("No pude aplicar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="field">
      <p className="tiny muted" style={{ marginBottom: 10, lineHeight: 1.5 }}>
        Si creaste planes <b>antes</b> de completar tu marca acá, podés
        propagar tus datos (nombre, tagline, logo, colores, redes) a esos
        planes con un clic. Útil cuando recién armás Mi marca y querés que tus
        plantillas ya hechas se vean coherentes.
      </p>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setOpen(true)}
        disabled={plansCount === 0}
      >
        <Icon name="sparkle" size={14} />{" "}
        {plansCount == null
          ? "Aplicar mi marca a planes existentes"
          : plansCount === 0
          ? "Todavía no tenés planes para sincronizar"
          : `Aplicar mi marca a mis ${plansCount} plan${plansCount === 1 ? "" : "es"}`}
      </button>

      {open && (
        <div className="al-modal-backdrop" onClick={() => !busy && setOpen(false)}>
          <div
            className="al-modal al-sync-brand"
            role="dialog"
            aria-label="Aplicar marca a planes"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="al-modal-hd">
              <div>
                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
                  Aplicar mi marca a planes existentes
                </h3>
                <div className="tiny muted" style={{ marginTop: 2 }}>
                  Vamos a actualizar el branding de tus {plansCount ?? "?"} planes.
                </div>
              </div>
              <button className="icon-btn" onClick={() => setOpen(false)} title="Cerrar" disabled={busy}>
                <Icon name="x" size={16} />
              </button>
            </div>
            <div className="al-sync-brand-body">
              <label
                className={"al-sync-brand-opt" + (mode === "fill_empty" ? " is-selected" : "")}
              >
                <input
                  type="radio"
                  name="brand-sync-mode"
                  checked={mode === "fill_empty"}
                  onChange={() => setMode("fill_empty")}
                  disabled={busy}
                />
                <div>
                  <b>Solo agregar lo que falta</b> <span className="tiny muted">(seguro, default)</span>
                  <div className="tiny muted" style={{ marginTop: 2, lineHeight: 1.4 }}>
                    Llena los campos vacíos del brand de cada plan con tus datos
                    actuales. Si un plan ya tiene logo o colores personalizados,
                    no se tocan.
                  </div>
                </div>
              </label>
              <label
                className={"al-sync-brand-opt" + (mode === "override" ? " is-selected" : "")}
              >
                <input
                  type="radio"
                  name="brand-sync-mode"
                  checked={mode === "override"}
                  onChange={() => setMode("override")}
                  disabled={busy}
                />
                <div>
                  <b>Reemplazar marca de los planes</b>{" "}
                  <span className="tiny" style={{ color: "var(--accent-magenta)" }}>(destructivo)</span>
                  <div className="tiny muted" style={{ marginTop: 2, lineHeight: 1.4 }}>
                    Sobrescribe nombre, tagline, logo, colores, instagram, web y
                    teléfono en cada plan con tus valores actuales. El estilo
                    editorial (tema, fuentes, densidad) se preserva.
                  </div>
                </div>
              </label>
            </div>
            <div className="al-sync-brand-foot">
              <button className="btn btn-ghost" onClick={() => setOpen(false)} disabled={busy}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={run}
                disabled={busy}
              >
                {busy ? "Aplicando…" : "Aplicar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card al-brand-section">
      <div className="card-title">{title}</div>
      <div className="al-brand-section-body">{children}</div>
    </div>
  );
}
